import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { useAudioPlayer } from '../useAudioPlayer';
import type { Track } from '../useAudioPlayer';
import { isCorrectGuess } from '../utils/guessHandlers';
import './retro.css';
import { SNIPPIT_API_URL, SNIPPIT_GAME_URL } from '../App';
import logo from '../logo.png';
import { DeezerFooter } from './LandingView';

type PlayerLocationState = {
  tracks: Track[];
  contextName: string;
  backPath: string;
  backgroundImage: string;
  artistId?: string;
  poolSize? : number;
};

type GuessState = 'guessing' | 'correct' | 'skipped' | 'timeout';

export async function setClipboard(text: string) {
  const type = "text/plain"
  let ClipboardData = {
    [type]: text
  };
  await navigator.clipboard.write([new ClipboardItem(ClipboardData)]);
}

function OutOfTracksModal({ onSelectDifficulty }: { onSelectDifficulty: (poolSize: number) => void }) {
  return (
    <div className="modal-overlay" onClick={() => {}}>
      <div className="beta-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <h2 style={{ marginBottom: '20px', textAlign: 'center' }}>OUT OF TRACKS</h2>
          <p style={{ textAlign: 'center', marginBottom: '24px', opacity: 0.8 }}>
            You've exhausted the available tracks at this difficulty level. Try a higher difficulty!
          </p>
          <a className="modal-link" href="/">
          <div className="modal-button">
            Start a new game!
          </div>
          </a>
          <div />
        </div>
      </div>
    </div>
  );
}

export function Player({ mode }: { mode: string }) {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { tracks: initialTracks, contextName, backPath, backgroundImage, artistId, poolSize = 1000 } = state as PlayerLocationState;

  const gameModeIsArtist: boolean = (mode === "artist")
  const isStreakMode: boolean = (mode === "streak")

  const [tracks, setTracks] = useState<Track[]>(initialTracks);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [streakEnded, setStreakEnded] = useState(false);
  const [showOutOfTracksModal, setShowOutOfTracksModal] = useState(false);
  const [tracksToExhaust, setTracksToExhaust] = useState<number>(-1);
  const [playedTrackIds, setPlayedTrackIds] = useState<Set<string>>(
    new Set(initialTracks.map(t => t.id))
  );
  const [timeoutCountdown, setTimeoutCountdown] = useState<number | null>(null);

  const {
    currentTrack,
    currentIndex,
    isPlaying,
    progress,
    toggle,
    setCurrentIndex,
  } = useAudioPlayer(tracks, !isStreakMode);

  const [guess, setGuess] = useState('');
  const [guessState, setGuessState] = useState<GuessState>('guessing');
  const [score, setScore] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  // Load more tracks when approaching the end in streak mode
  useEffect(() => {

    // Fetch more tracks for streak mode
    const fetchMoreTracks = async () => {
      if (!isStreakMode || !artistId) {
        return;
      }

      setIsLoadingMore(true);
      const excludeIdsParam = Array.from(playedTrackIds).join(',');
      const fetchUrl = `${SNIPPIT_API_URL}/api/snippets/${artistId}?count=5&excludeIds=${excludeIdsParam}&poolSize=${poolSize ?? 1000}`;

      try {
        const response = await fetch(fetchUrl);
        if (response.ok) {
          const newTracks = await response.json();

          if (newTracks.length === 0) {
            console.warn('No more tracks available');
            setTracksToExhaust(0);
            return;
          }

          if (newTracks.length < 5) {
            setTracksToExhaust(newTracks.length);
          }

          setTracks(prev => [...prev, ...newTracks]);
          newTracks.forEach((track: Track) => {
            setPlayedTrackIds(prev => new Set([...prev, track.id]));
          });
        }
      } catch (error) {
        console.error('Fetch error:', error);
        setStreakEnded(true);
      } finally {
        setIsLoadingMore(false);
      }
    };

    if (isStreakMode && currentIndex >= tracks.length - 3 && !isLoadingMore && !streakEnded && tracksToExhaust === -1) {
      fetchMoreTracks();
    }
  }, [currentIndex, isStreakMode, tracks.length, isLoadingMore, streakEnded, artistId, playedTrackIds, poolSize, tracksToExhaust]);

  // Check if all exhausted tracks have been played
  useEffect(() => {
    if (isStreakMode && tracksToExhaust !== -1) {
      // If tracksToExhaust is 0, show modal when trying to go past the last track
      if (tracksToExhaust === 0 && currentIndex >= tracks.length) {
        setShowOutOfTracksModal(true);
      } 
      // If tracksToExhaust > 0, show modal after playing through those remaining tracks
      else if (tracksToExhaust > 0 && currentIndex > tracks.length - tracksToExhaust) {
        setShowOutOfTracksModal(true);
      }
    }
  }, [currentIndex, tracksToExhaust, isStreakMode, tracks.length]);

  // Timeout countdown timer
  useEffect(() => {
    if (isStreakMode && guessState === 'guessing' && timeoutCountdown !== null) {
      if (timeoutCountdown <= 0) {
        setGuessState('timeout');
        setStreakEnded(true);
        setTimeoutCountdown(null);
        return;
      }

      const timer = setTimeout(() => {
        setTimeoutCountdown(prev => (prev !== null ? prev - 1 : null));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [timeoutCountdown, guessState, isStreakMode]);

  // Start countdown when track finishes
  useEffect(() => {
    if (isStreakMode && progress === 100 && guessState === 'guessing') {
      setTimeoutCountdown(5);
    }
  }, [progress, guessState, isStreakMode]);

  useEffect(() => {
    setGuess('');
    setGuessState('guessing');
    setTimeoutCountdown(null);
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [currentIndex]);

  useEffect(() => {
    if (guessState === 'correct') {
      const timer = setTimeout(() => {
        // Check if we're at the last track and have exhausted all tracks
        if (isStreakMode && tracksToExhaust !== -1 && currentIndex === tracks.length - 1) {
          setShowOutOfTracksModal(true);
        } else if (currentIndex < tracks.length - 1 || isStreakMode) {
          setCurrentIndex(currentIndex + 1);
        }
      }, 2000);
      return () => clearTimeout(timer);
    } else if (guessState === 'skipped' || guessState === 'timeout') {
      if (isStreakMode) {
        // Streak ends on skip or timeout
        setStreakEnded(true);
      } else {
        const timer = setTimeout(() => {
          if (currentIndex < tracks.length - 1) {
            setCurrentIndex(currentIndex + 1);
          }
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [guessState, currentIndex, isStreakMode, tracks.length, setCurrentIndex, tracksToExhaust]);

  const handleSelectDifficulty = (poolSize: number) => {
    setShowOutOfTracksModal(false);
    // Reset streak and load new tracks with different difficulty
    setScore(0);
    setTracks(initialTracks);
    setPlayedTrackIds(new Set(initialTracks.map(t => t.id)));
    setStreakEnded(false);
    setCurrentIndex(0);
  };

  if (!currentTrack) return null;

  const handleGuess = () => {
    if (!guess.trim() || guessState !== 'guessing') return;
    const correctAnswer = (gameModeIsArtist) ? currentTrack.artist : currentTrack.title;

    if (isCorrectGuess(guess, correctAnswer, mode)) {
      setGuessState('correct');
      setScore(s => s + 1);
      setTimeoutCountdown(null);
    } else if (isStreakMode) {
      // Wrong guess ends the streak
      setGuessState('skipped');
      setStreakEnded(true);
      setTimeoutCountdown(null);
    } else {
      // Non-streak mode: show error feedback and clear input
      setGuess('');
      inputRef.current?.classList.add('input-error');
      setTimeout(() => {
        inputRef.current?.classList.remove('input-error');
      }, 600);
      inputRef.current?.focus();
    }
  };

  const handleSkip = () => {
    if (guessState !== 'guessing') return;
    if (isStreakMode) {
      setStreakEnded(true);
    }
    setGuessState('skipped');
    setTimeoutCountdown(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleGuess();
  };

  const formatDuration = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const isRevealed = guessState !== 'guessing';
  const isLastTrack = currentIndex === tracks.length - 1;
  const isFinished = streakEnded || (isRevealed && isLastTrack && !isStreakMode);

  return (
    <div
      className="landing-root"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      <div className="grain" />
      <div className="vignette" />

      {showOutOfTracksModal && isStreakMode && (
        <OutOfTracksModal onSelectDifficulty={handleSelectDifficulty} />
      )}

      <div className="player-inner">
        <div className="quiz-header">
          <button className="back-btn" onClick={() => navigate(backPath)}>← BACK</button>
          <div className="score-display">
            <span className="score-label">{isStreakMode ? 'STREAK' : 'SCORE'}</span>
            <span className="score-value">{score}{!isStreakMode && `/${tracks.length}`}</span>
          </div>
        </div>

        <div className="player-artist">{contextName.toUpperCase()}</div>

        <div className="player-cassette">
          <div className={`reel reel-left ${isPlaying ? 'reel-spin' : ''}`}>
            <div className="reel-hub" />
          </div>
          <div className="player-label">
            <div className="track-number">
              {String(currentIndex + 1).padStart(2, '0')} {!isStreakMode && `/ ${String(tracks.length).padStart(2, '0')}`}
            </div>
            <div className={`track-title ${guessState === 'correct' ? 'title-correct' : guessState === 'skipped' || guessState === 'timeout' ? 'title-skipped' : 'title-hidden'}`}>
              {isRevealed ? `${currentTrack.title}` : '???'}
            </div>
            <div className={`track-artist ${guessState === 'correct' ? 'title-correct' : guessState === 'skipped' || guessState === 'timeout' ? 'title-skipped' : 'title-hidden'}`}>
              {isRevealed ? `by  ${currentTrack.artist}` : '???'}
            </div>
            <img
              className="track-artist-image"
              src={isRevealed ? currentTrack.artworkPath : logo}
              alt="artwork of the track"
            />
            <div className="track-duration">{formatDuration(currentTrack.duration)}</div>
          </div>
          <div className={`reel reel-right ${isPlaying ? 'reel-spin reel-spin-reverse' : ''}`}>
            <div className="reel-hub" />
          </div>
        </div>

        <div className="progress-track">
          <div className="progress-fill" style={{ width: `${progress}%` }} />
        </div>

        <button className="ctrl-btn ctrl-play" onClick={toggle}>
          {isPlaying ? '⏸' : '▶'}
        </button>

        {!isFinished && (
          <div className="quiz-input-area">
            {guessState === 'guessing' ? (
              <>
                <div className="field-group" style={{ width: '100%' }}>
                  <label className="field-label">
                    GUESS THE {gameModeIsArtist ? 'ARTIST' : 'SONG'}
                    {timeoutCountdown !== null && isStreakMode && (
                      <span className="timeout-countdown"> ({timeoutCountdown}s)</span>
                    )}
                  </label>
                  <div className="search-row">
                    <input
                      ref={inputRef}
                      className="retro-input quiz-retro-input"
                      type="text"
                      placeholder="type your guess..."
                      value={guess}
                      onChange={e => setGuess(e.target.value)}
                      onKeyDown={handleKeyDown}
                      autoFocus
                      disabled={timeoutCountdown !== null && timeoutCountdown <= 0}
                    />
                    <button className="search-btn" onClick={handleGuess}>✓</button>
                  </div>
                </div>
                <button className="skip-btn" onClick={handleSkip}>{isStreakMode ? 'END STREAK →' : 'SKIP →'}</button>
              </>
            ) : (
              <div className={`reveal-msg ${guessState === 'correct' ? 'reveal-correct' : 'reveal-skipped'}`}>
                {guessState === 'correct' ? '✓ CORRECT!' : guessState === 'timeout' ? '⏱ TIME\'S UP' : isStreakMode ? '✗ STREAK ENDED' : '✗ SKIPPED'}
                {!isFinished && <span className="reveal-next"> next up...</span>}
              </div>
            )}
          </div>
        )}

        {isFinished && (
          <div className="finished-block">
            <div className="finished-title">{isStreakMode ? "STREAK" : "GAME"} OVER</div>
            <div className="finished-score">{isStreakMode ? `${score} in a row!` : `${score} out of ${tracks.length}!`}</div>
            <button className="play-btn" onClick={() => navigate(backPath)}>PLAY AGAIN</button>
            <button className="play-btn" onClick={async () => {
              let clipText :string = ""
              if (isStreakMode) {
                clipText = `I scored ${score} in streak mode correctly naming ${contextName} songs!\n${SNIPPIT_GAME_URL}`
              } else {
                clipText = `I scored ${score}/${tracks.length} naming ${contextName} songs!\n${SNIPPIT_GAME_URL}`
              }
              
              setClipboard(clipText);

            }}>SHARE</button>
          </div>
        )}

        {DeezerFooter()}
      </div>
    </div>
  );
}

export default function PlayerView() {
  const { gameMode } = useParams();

  return (
    <>
      <Player mode={gameMode ?? ""} />
    </>
  )
}